import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import _ from 'lodash';

// comps
import { Box, Typography } from '@mui/material';
import DialogTemplate from './dialogTemplate';
import GlobalInput from '../global/input';
import GlobalButton from '../global/button';
import GlobalDropdown from '../global/dropdown';
import ToastMessage from '../global/toastMessage';

// constants
import { defaultDropdownArr } from '../../constants';
import { colors } from '../../theme';

// icons
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-icon.svg';

const InstructorDialog = (props: any) => {
    const { data, onSave, onClose, programs, onCreate, isAdmin, instructorId } = props;
    const { adminNote, bcitId, department, email, firstName, isActive, lastName, middleName, office, optedOutOfPublicSite, phone, prefName, publicNote, title } = data;

    const isCreate = !data.instructorId;
    const [errorMsg, setErrorMsg] = useState(null);
    const [requiredFields, setRequiredFields] = useState([]);
    const [isClicked, setIsClicked] = useState(false);

    const [dataItem, setDataItem] = useState({
        ...data,
        adminNote,
        bcitId,
        department,
        email,
        firstName,
        isActive,
        lastName,
        middleName,
        office,
        optedOutOfPublicSite,
        phone,
        prefName,
        publicNote,
        title,
    });

    const onChange = (name: any, value: any) => {
        setErrorMsg(null);
        setDataItem({
            ...dataItem,
            [name]: name === 'bcitId' ? value.toUpperCase() : value,
        });
    };

    const requiredFieldsArr = [
        {
            name: 'BCIT ID',
            value: dataItem.bcitId,
        },
        {
            name: 'First Name',
            value: dataItem.firstName,
        },
        {
            name: 'Last Name',
            value: dataItem.lastName,
        },
        {
            name: 'Email',
            value: dataItem.email,
        },
        {
            name: 'Position',
            value: dataItem.title,
        },
        {
            name: 'Program',
            value: dataItem?.department?.departmentCode,
        },
    ];

    useEffect(() => {
        // Check if required fields are filled (when creating)
        setRequiredFields(requiredFieldsArr.filter((field) => field.value === '' || !field.value).map((field) => field.name));
    }, [dataItem]);

    const handleSave = () => {
        // Convert isActive and optedOutOfPublicSite to boolean
        const isIsActiveObj = dataItem.isActive?.id ? 'isActive' : false;
        const isOptedOutOfPublicSiteObj = dataItem.optedOutOfPublicSite?.id ? 'optedOutOfPublicSite' : false;

        let item = { ...dataItem };
        if (isIsActiveObj) {
            item = { ...item, isActive: dataItem.isActive.value };
        }

        if (isOptedOutOfPublicSiteObj) {
            item = { ...item, optedOutOfPublicSite: dataItem.optedOutOfPublicSite.value };
        }

        setIsClicked((state) => !state);

        if (requiredFields.length === 0) {
            if (item.bcitId.length !== 9) {
                setErrorMsg('The instructor must have a valid A0 number.');
                return;
            }
            if (!item.email.endsWith('@bcit.ca')) {
                setErrorMsg('The instructor must have a valid @bcit.ca email address.');
                return;
            } else {
                if (isCreate) onCreate(item);
                else {
                    // If fields were NOT edited (= if the object is unchanged)
                    if (_.isEqual(item, data)) {
                        toast.warning(`There were no changes made to instructor ${item.firstName} ${item.lastName}.`);
                        onClose();
                    } else onSave(item);
                }
            }
        } else setErrorMsg(`The required fields must be filled in order to proceed.`);
    };

    const handleErrorState = (name: string) => {
        if (isClicked) {
            return requiredFields?.includes(name);
        }
    };

    const renderContent = () => {
        return (
            <Container>
                {errorMsg && (
                    <FlexBox>
                        <ToastMessage isError msg={errorMsg} icon={<WarningIcon />} />
                    </FlexBox>
                )}
                <FlexBox>
                    <InputBox sx={{ '& > *': { mr: '10px' }, flex: 1 }}>
                        <Label>BCIT ID <span>*</span></Label>
                        <GlobalInput
                            isRequired
                            isError={handleErrorState('BCIT ID')}
                            placeholder="A01123456"
                            value={dataItem.bcitId}
                            name={'bcitId'}
                            onChange={onChange}
                            inputProps={{ maxLength: 9 }}
                        />
                        <Label>First Name <span>*</span></Label>
                        <GlobalInput isRequired isError={handleErrorState('First Name')} placeholder="John" value={dataItem.firstName} name={'firstName'} onChange={onChange} />
                        <Label>Last name <span>*</span></Label>
                        <GlobalInput isRequired isError={handleErrorState('Last Name')} placeholder="Doe" value={dataItem.lastName} name={'lastName'} onChange={onChange} />
                        <Label>Phone</Label>
                        <GlobalInput placeholder="(000)-111-2222" value={dataItem.phone} name={'phone'} onChange={onChange} />
                        <Label>Office</Label>
                        <GlobalInput placeholder="SE16 000" value={dataItem.office} name={'office'} onChange={onChange} />
                        <Label>Opt Out of Public Timetables</Label>
                        <GlobalDropdown
                            width="50px"
                            placeholder={!optedOutOfPublicSite ? 'No' : 'Yes'}
                            data={defaultDropdownArr}
                            dataItemKey={'optedOutOfPublicSite'}
                            name={'label'}
                            field={'value'}
                            value={dataItem.optedOutOfPublicSite}
                            onChange={onChange}
                        />
                    </InputBox>
                    <InputBox sx={{ flex: 1 }}>
                    <Label>Position <span>*</span></Label>
                        <GlobalInput isRequired isError={handleErrorState('Position')} placeholder="Instructor" value={dataItem.title} name={'title'} onChange={onChange} />
                        <Label>Middle Name</Label>
                        <GlobalInput placeholder="Middle Name" value={dataItem.middleName} name={'middleName'} onChange={onChange} />
                        <Label>Preferred Name</Label>
                        <GlobalInput placeholder="Preferred Name" value={dataItem.prefName} name={'prefName'} onChange={onChange} />
                        <Label>Email <span>*</span></Label>
                        <GlobalInput isRequired isError={handleErrorState('Email')} placeholder="email@bcit.ca" value={dataItem.email} name={'email'} onChange={onChange} />
                        <Label>Program <span>*</span></Label>
                        <GlobalDropdown
                            isRequired
                            isError={handleErrorState('Program')}
                            placeholder={isCreate ? 'Select a program' : ''}
                            disabled={!isCreate && !isAdmin}
                            data={programs}
                            field={'departmentID'}
                            name={'departmentCode'}
                            dataItemKey={'department'}
                            value={dataItem.department}
                            onChange={onChange}
                        />
                        <Label>Is Active <span>*</span></Label>
                        <GlobalDropdown
                            isRequired
                            disabled={!isAdmin}
                            width="50px"
                            placeholder={!isActive ? 'No' : 'Yes'}
                            data={defaultDropdownArr}
                            dataItemKey={'isActive'}
                            name={'label'}
                            field={'value'}
                            value={dataItem.isActive}
                            onChange={onChange}
                        />
                    </InputBox>
                </FlexBox>
                {instructorId === data?.instructorId && (
                    <>
                        <Label>Public Notes</Label>
                        <GlobalInput
                            multiline
                            minRows={2}
                            placeholder="Public notes (Max 100 characters)"
                            value={dataItem.publicNote}
                            name={'publicNote'}
                            onChange={onChange}
                            sx={{ mb: '1em' }}
                        />
                    </>
                )}
                {isAdmin &&
                    (
                        <>
                            <Label>Admin Notes</Label>
                            <GlobalInput multiline minRows={2} placeholder="Admin notes (Max 100 characters)" value={dataItem.adminNote} name={'adminNote'} onChange={onChange} />
                        </>
                    )
                }
                <FlexBox sx={{ mt: '2em' }}>
                    <GlobalButton text="cancel" variant="outlined" onClick={onClose} />
                    <GlobalButton text={isCreate ? 'create' : 'save'} variant="contained" onClick={handleSave} />
                </FlexBox>
            </Container>
        );
    };
    return (
        <>
            <DialogTemplate {...props} title={'Instructor'} renderContent={renderContent} />
        </>
    );
};

export default InstructorDialog;

const Container = styled(Box)`
    && {
        width: 100%;
        padding: 1em 0px;
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
`;

const InputBox = styled(Box)`
    && {
        & > .MuiBox-root {
            margin-bottom: 1em;
        }
    }
`;

const Label = styled.div`
    color: #0085E6;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 2px;

    & > span {
        color: #D73948;
    }
`
